import { useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarClock, ExternalLink, PoundSterling, Repeat, TrendingUp, Gift, ArrowUpRight, ArrowDownRight } from "lucide-react";
import {
  CPE_FEED_URL, currentRun, formatMonth, shiftMonth,
  type ConcessionProduct,
} from "@/lib/claims/concessionFeedData";

interface Props {
  product: ConcessionProduct;
  claimPackSize: number;
  dark: boolean;
}

const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export function ConcessionInsightPanel({ product, claimPackSize, dark }: Props) {
  const latestMonth = product.latestMonth;
  const activePacks = Array.from(new Set(product.entries.filter(x => x.month === latestMonth).map(x => x.packSize))).sort((a, b) => a - b);
  const [pack, setPack] = useState(activePacks.includes(claimPackSize) ? claimPackSize : activePacks[0]);

  const packEntries = product.entries.filter(x => x.packSize === pack).sort((a, b) => a.month.localeCompare(b.month));
  const current = packEntries.find(x => x.month === latestMonth);
  if (!current) return null;

  const dtKnown = product.drugTariffPrices[pack] > 0;
  const dt = dtKnown ? product.drugTariffPrices[pack] : 0;
  const uplift = current.price - dt;
  const upliftPct = dt > 0 ? (uplift / dt) * 100 : 0;
  const run = currentRun(product.entries, pack, latestMonth);
  const windowStart = shiftMonth(latestMonth, -11);
  const last12 = packEntries.filter(x => x.month >= windowStart).length;

  const chartData = Array.from({ length: 12 }, (_, i) => {
    const month = shiftMonth(windowStart, i);
    const entry = packEntries.find(x => x.month === month);
    return { month, label: formatMonth(month).replace(" 20", " '"), price: entry?.price ?? null };
  });

  const muted = dark ? "text-gray-400" : "text-gray-500";
  const faint = dark ? "text-gray-500" : "text-gray-400";
  const card = `rounded-lg border px-2.5 py-2 ${dark ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`;
  const amberBar = dark ? "#f59e0b" : "#d97706";

  return (
    <div className={`mt-3 rounded-lg border p-3 space-y-3 ${dark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50/40"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <PoundSterling className={`w-3.5 h-3.5 ${dark ? "text-amber-400" : "text-amber-600"}`} />
            <span className={`text-[10px] font-semibold uppercase tracking-wider ${dark ? "text-amber-400" : "text-amber-700"}`}>Active price concession</span>
          </div>
          <p className={`text-xs font-semibold mt-0.5 ${dark ? "text-gray-100" : "text-gray-900"}`}>{product.drug}</p>
          <p className={`text-[10px] ${faint}`}>Granted in “{current.grantedIn.title}” · {formatDate(current.grantedIn.publishedAt)}</p>
        </div>
        {activePacks.length > 1 && (
          <div className={`flex shrink-0 rounded-md border p-0.5 ${dark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"}`}>
            {activePacks.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPack(p)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded cursor-pointer ${p === pack ? (dark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-800") : muted}`}
              >
                Pack of {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className={card}>
          <p className={`text-[9px] font-semibold uppercase tracking-wider ${muted}`}>Concession price</p>
          <p className={`text-sm font-bold ${dark ? "text-amber-300" : "text-amber-700"}`}>£{current.price.toFixed(2)}</p>
          <p className={`text-[9px] ${faint}`}>
            {current.previousPrice !== undefined ? <>Revised from £{current.previousPrice.toFixed(2)}</> : <>Pack of {pack} · {formatMonth(current.month, "long")}</>}
          </p>
        </div>
        <div className={card}>
          <p className={`text-[9px] font-semibold uppercase tracking-wider ${muted}`}>Drug Tariff price</p>
          <p className={`text-sm font-bold ${dark ? "text-gray-100" : "text-gray-900"}`}>{dtKnown ? `£${dt.toFixed(2)}` : "—"}</p>
          <p className={`text-[9px] ${faint}`}>Normal reimbursement</p>
        </div>
        <div className={card}>
          <p className={`text-[9px] font-semibold uppercase tracking-wider ${muted}`}>Difference / pack</p>
          {dtKnown ? (
            <>
              <p className={`text-sm font-bold flex items-center gap-0.5 ${uplift >= 0 ? (dark ? "text-emerald-300" : "text-emerald-700") : dark ? "text-red-300" : "text-red-600"}`}>
                {uplift >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                {uplift >= 0 ? "+" : "−"}£{Math.abs(uplift).toFixed(2)}
              </p>
              <p className={`text-[9px] ${faint}`}>{uplift >= 0 ? "+" : ""}{upliftPct.toFixed(0)}% vs Drug Tariff</p>
            </>
          ) : (
            <>
              <p className={`text-sm font-bold ${dark ? "text-gray-100" : "text-gray-900"}`}>—</p>
              <p className={`text-[9px] ${faint}`}>No Drug Tariff price</p>
            </>
          )}
        </div>
        <div className={card}>
          <p className={`text-[9px] font-semibold uppercase tracking-wider ${muted}`}>On concession</p>
          <p className={`text-sm font-bold ${dark ? "text-gray-100" : "text-gray-900"}`}>{run.months} {run.months === 1 ? "month" : "months"}</p>
          <p className={`text-[9px] ${faint}`}>Since {formatMonth(run.since, "long")} · {last12} of last 12</p>
        </div>
      </div>

      {(current.rolledOverTo || current.previousPrice !== undefined || packEntries.some(x => x.topUp)) && (
        <div className="flex flex-wrap gap-1.5">
          {current.rolledOverTo && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${dark ? "bg-blue-500/15 text-blue-300" : "bg-blue-100 text-blue-700"}`}>
              <Repeat className="w-3 h-3" /> Rolled over to {formatMonth(current.rolledOverTo, "long")}
            </span>
          )}
          {current.previousPrice !== undefined && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
              <TrendingUp className="w-3 h-3" /> Price increased mid-month
            </span>
          )}
          {packEntries.some(x => x.topUp) && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${dark ? "bg-purple-500/15 text-purple-300" : "bg-purple-100 text-purple-700"}`}>
              <Gift className="w-3 h-3" /> Retrospective top-up paid
            </span>
          )}
        </div>
      )}

      <div className={`rounded-lg border p-2 ${dark ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-white"}`}>
        <div className="flex items-center justify-between mb-1 px-1">
          <span className={`text-[10px] font-semibold ${dark ? "text-gray-300" : "text-gray-700"}`}>Concession price — last 12 months</span>
          <span className={`flex items-center gap-2 text-[9px] ${faint}`}>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: amberBar }} />Concession</span>
            <span className="flex items-center gap-1"><span className={`inline-block w-3 border-t border-dashed ${dark ? "border-gray-400" : "border-gray-500"}`} />Drug Tariff</span>
          </span>
        </div>
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid vertical={false} stroke={dark ? "#374151" : "#e5e7eb"} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: dark ? "#9ca3af" : "#6b7280" }} tickLine={false} axisLine={false} interval={0} />
              <YAxis tick={{ fontSize: 9, fill: dark ? "#9ca3af" : "#6b7280" }} tickLine={false} axisLine={false} tickFormatter={v => `£${v}`} />
              <Tooltip
                cursor={{ fill: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }}
                contentStyle={{ fontSize: 11, borderRadius: 8, background: dark ? "#111827" : "#fff", border: `1px solid ${dark ? "#374151" : "#e5e7eb"}` }}
                formatter={(v: number | null) => (v === null ? ["Not on concession", ""] : [`£${v.toFixed(2)}`, "Concession"])}
                labelFormatter={(_, p) => (p?.[0] ? formatMonth(p[0].payload.month, "long") : "")}
              />
              {dtKnown && <ReferenceLine y={dt} stroke={dark ? "#9ca3af" : "#6b7280"} strokeDasharray="4 3" />}
              <Bar dataKey="price" radius={[3, 3, 0, 0]} maxBarSize={22}>
                {chartData.map(d => (
                  <Cell key={d.month} fill={amberBar} fillOpacity={d.month === latestMonth ? 1 : 0.55} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={`overflow-hidden rounded-md border ${dark ? "border-gray-700" : "border-gray-200"}`}>
        <table className="w-full text-[11px]">
          <thead className={dark ? "bg-gray-900/60" : "bg-gray-50"}>
            <tr className={`text-left text-[9px] uppercase tracking-wider ${muted}`}>
              <th className="px-2.5 py-1.5 font-semibold">Month</th>
              <th className="px-2.5 py-1.5 font-semibold text-right">Price</th>
              <th className="px-2.5 py-1.5 font-semibold text-right">Change</th>
              <th className="px-2.5 py-1.5 font-semibold">CPE announcement</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${dark ? "divide-gray-700 bg-gray-900/20" : "divide-gray-100 bg-white"}`}>
            {[...packEntries].reverse().map((x, i, arr) => {
              const prior = arr[i + 1];
              const change = prior ? x.price - prior.price : null;
              const isLatest = x.month === latestMonth;
              return (
                <tr key={x.month} className={isLatest ? (dark ? "bg-amber-500/10" : "bg-amber-50") : undefined}>
                  <td className={`px-2.5 py-1.5 whitespace-nowrap ${dark ? "text-gray-100" : "text-gray-800"}`}>
                    <span className="font-medium">{formatMonth(x.month)}</span>
                    {isLatest && <span className={`ml-1 text-[9px] font-semibold ${dark ? "text-amber-300" : "text-amber-700"}`}>Current</span>}
                  </td>
                  <td className={`px-2.5 py-1.5 text-right tabular-nums font-semibold ${dark ? "text-gray-100" : "text-gray-900"}`}>£{x.price.toFixed(2)}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">
                    {change === null || Math.abs(change) < 0.005
                      ? <span className={faint}>—</span>
                      : <span className={change > 0 ? (dark ? "text-emerald-300" : "text-emerald-600") : dark ? "text-red-300" : "text-red-600"}>{change > 0 ? "+" : "−"}£{Math.abs(change).toFixed(2)}</span>}
                  </td>
                  <td className="px-2.5 py-1.5">
                    {x.grantedIn.link ? (
                      <a href={x.grantedIn.link} target="_blank" rel="noopener noreferrer" className={`hover:underline ${dark ? "text-gray-300" : "text-gray-700"}`}>{x.grantedIn.title}</a>
                    ) : (
                      <p className={dark ? "text-gray-300" : "text-gray-700"}>{x.grantedIn.title}</p>
                    )}
                    <p className={`text-[9px] ${faint}`}>
                      {formatDate(x.grantedIn.publishedAt)}
                      {x.previousPrice !== undefined && <> · revised from £{x.previousPrice.toFixed(2)}</>}
                      {x.rolledOverTo && <> · rolled over to {formatMonth(x.rolledOverTo)}</>}
                      {x.topUp && <> · +£{x.topUp.amount.toFixed(2)} top-up ({formatDate(x.topUp.announcedIn.publishedAt)})</>}
                    </p>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={`flex items-start justify-between gap-3 text-[9px] ${faint}`}>
        <p className="flex items-start gap-1">
          <CalendarClock className="w-3 h-3 shrink-0 mt-px" />
          Concessions only apply to the dispensing month they're granted for (unless rolled over). No extra endorsement is needed.
        </p>
        <a href={CPE_FEED_URL} target="_blank" rel="noopener noreferrer" className={`shrink-0 inline-flex items-center gap-0.5 font-medium hover:underline ${dark ? "text-amber-300" : "text-amber-700"}`}>
          CPE source <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
    </div>
  );
}
